import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  Modal,
  TextField,
  Typography
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { getManagedAccounts } from "../../api/account";
import { useTranslationHook } from "../../hooks";
import { Account } from "../../types/model/account";

interface ManagedAccountSelectorProps {
  open: boolean;
  onSelect: (accountId: number) => void;
  onCancel: () => void;
}

const ManagedAccountSelector = ({
  open,
  onSelect,
  onCancel,
}: ManagedAccountSelectorProps) => {
  const { t } = useTranslationHook();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchAccounts = async () => {
      setLoading(true);
      try {
        const response = await getManagedAccounts();
        setAccounts(response);
        enqueueSnackbar(t("generic.data-loaded"), { variant: "success" });
      } catch (error) {
        console.error(error);
        enqueueSnackbar(t("error.data-not-loaded"), { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [open]);

  const filteredAccounts = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return accounts.filter((acc) =>
      acc.name.toLowerCase().includes(q)
    );
  }, [searchQuery, accounts]);

  let content;

  if (loading) {
    content = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (filteredAccounts.length === 0) {
    content = (
      <Box
        sx={{
          textAlign: "center",
          mt: 6,
          opacity: 0.6,
        }}
      >
        <SearchIcon sx={{ fontSize: 40, mb: 1 }} />
        <Typography variant="body2">
          {t("manager.not-available")}
        </Typography>
      </Box>
    );
  } else {
    content = filteredAccounts.map((account) => (
      <Card
        key={account.id}
        sx={{
          mb: 1.5,
          borderRadius: 2,
          transition: "all 0.2s",
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardActionArea onClick={() => onSelect(account.id)}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600}>
              {account.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {account.email}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    ));
  }

  return (
    <Modal open={open} onClose={onCancel}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          height: "50vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("projects.select-account")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("projects.select-account-description")}
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder={t("generic.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ opacity: 0.6 }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            pb: 2,
            minHeight: 0,
          }}
        >
          {content}
        </Box>

        <Divider />

        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onCancel}>
            {t("generic.cancel")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ManagedAccountSelector;